<template>
  <div class="auto-scroll-table" :class="'auto-scroll-table--' + variant">
    <table class="auto-scroll-head">
      <thead>
        <tr>
          <th v-for="col in columns" :key="col.key">{{ col.label }}</th>
        </tr>
      </thead>
    </table>
    <div
      class="auto-scroll-viewport"
      ref="viewport"
      :style="viewportStyle"
      @mouseenter="paused = true"
      @mouseleave="paused = false"
    >
      <div v-if="!rows.length" class="auto-scroll-empty">{{ emptyText }}</div>
      <div v-else class="auto-scroll-inner" ref="inner">
        <table>
          <tbody>
            <tr v-for="(row, i) in displayRows" :key="rowKey(row, i)">
              <td v-for="col in columns" :key="col.key + '-' + i">
                {{ cellText(row, col) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * 大屏表格自动轮播（内容超出可视区时无缝向上滚动）。最后更新：2026-07-09
 */
export default {
  name: "ScreenAutoScrollTable",
  props: {
    columns: {
      type: Array,
      default: () => [],
    },
    rows: {
      type: Array,
      default: () => [],
    },
    emptyText: {
      type: String,
      default: "暂无",
    },
    speed: {
      type: Number,
      default: 28,
    },
    bodyHeight: {
      type: Number,
      default: 188,
    },
    variant: {
      type: String,
      default: "default",
    },
  },
  data() {
    return {
      paused: false,
      scrollActive: false,
      displayRows: [],
      rafId: null,
      scrollY: 0,
      loopHeight: 0,
      lastTick: 0,
    };
  },
  computed: {
    viewportStyle() {
      return {
        maxHeight: `${this.bodyHeight}px`,
        height: `${this.bodyHeight}px`,
      };
    },
  },
  watch: {
    rows: {
      handler() {
        this.$nextTick(this.resetScroll);
      },
      deep: true,
    },
    bodyHeight() {
      this.$nextTick(this.resetScroll);
    },
  },
  mounted() {
    this.$nextTick(this.resetScroll);
    window.addEventListener("resize", this.resetScroll);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.resetScroll);
    this.stopScroll();
  },
  methods: {
    cellText(row, col) {
      if (typeof col.formatter === "function") return col.formatter(row);
      return row[col.key] != null && row[col.key] !== "" ? row[col.key] : "-";
    },
    rowKey(row, index) {
      const name = row.stuName || row.name || "";
      return `${name}-${index}`;
    },
    stopScroll() {
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
    },
    resetScroll() {
      this.stopScroll();
      this.scrollY = 0;
      this.scrollActive = false;
      this.loopHeight = 0;

      const list = this.rows || [];
      if (!list.length) {
        this.displayRows = [];
        return;
      }

      const viewport = this.$refs.viewport;
      const inner = this.$refs.inner;
      if (!viewport || !inner) {
        this.displayRows = list.slice();
        return;
      }

      inner.style.transform = "translateY(0)";
      this.displayRows = list.slice();

      this.$nextTick(() => {
        const contentHeight = inner.scrollHeight;
        const viewportHeight = viewport.clientHeight;
        if (contentHeight <= viewportHeight + 2) {
          this.scrollActive = false;
          return;
        }

        this.displayRows = list.concat(list);
        this.scrollActive = true;

        this.$nextTick(() => {
          this.loopHeight = inner.scrollHeight / 2;
          if (this.loopHeight <= 0) return;
          this.lastTick = performance.now();
          this.rafId = requestAnimationFrame(this.tickScroll);
        });
      });
    },
    tickScroll(now) {
      if (!this.scrollActive) return;
      const inner = this.$refs.inner;
      if (!inner) return;

      if (!this.paused) {
        const delta = Math.max(0, now - this.lastTick);
        this.scrollY += (this.speed * delta) / 1000;
        if (this.loopHeight > 0 && this.scrollY >= this.loopHeight) {
          this.scrollY -= this.loopHeight;
        }
        inner.style.transform = `translateY(-${this.scrollY}px)`;
      }
      this.lastTick = now;
      this.rafId = requestAnimationFrame(this.tickScroll);
    },
  },
};
</script>

<style scoped>
.auto-scroll-table {
  width: 100%;
}

.auto-scroll-head {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  table-layout: fixed;
}

.auto-scroll-head th {
  background: #123456;
  color: #9ec9ff;
  padding: 8px 6px;
  text-align: left;
  font-weight: normal;
}

.auto-scroll-viewport {
  overflow: hidden;
  position: relative;
}

.auto-scroll-inner {
  will-change: transform;
}

.auto-scroll-inner table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  table-layout: fixed;
}

.auto-scroll-inner td {
  padding: 7px 6px;
  border-bottom: 1px solid rgba(58, 110, 165, 0.35);
  color: #e8f4ff;
  word-break: break-all;
}

.auto-scroll-empty {
  text-align: center;
  color: #6a8aaa;
  padding: 24px 0;
  font-size: 13px;
}

/* 大屏专用：更高行高、更清晰层次 */
.auto-scroll-table--screen .auto-scroll-head th {
  padding: 14px 16px;
  font-size: 15px;
  font-weight: 500;
  letter-spacing: 1px;
  color: #cbd5e1;
  background: linear-gradient(180deg, rgba(30, 58, 138, 0.35) 0%, rgba(15, 23, 42, 0.9) 100%);
  border-bottom: 1px solid rgba(59, 130, 246, 0.2);
}

.auto-scroll-table--screen .auto-scroll-inner table {
  font-size: 16px;
}

.auto-scroll-table--screen .auto-scroll-inner td {
  padding: 16px 16px;
  line-height: 1.5;
  min-height: 52px;
  box-sizing: border-box;
  color: #e2e8f0;
  border-bottom: 1px solid rgba(51, 65, 85, 0.5);
}

.auto-scroll-table--screen .auto-scroll-inner tbody tr:nth-child(even) td {
  background: rgba(59, 130, 246, 0.04);
}

.auto-scroll-table--screen .auto-scroll-empty {
  padding: 56px 0;
  font-size: 15px;
  color: #64748b;
}
</style>
